import React, { forwardRef, useId } from 'react';
import clsx from 'clsx';

export interface SelectOption {
  label: string;
  value: string;
}

export interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  label?: string;
  error?: string;
  helperText?: string;
  options: SelectOption[];
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, error, helperText, options, className, id, ...props }, ref) => {
    const generatedId = useId();
    const selectId = id || `select-${generatedId}`;

    return (
      <div className="w-full">
        {label ? (
          <label htmlFor={selectId} className="mb-2 block text-sm font-medium text-agentic-text-primary">
            {label}
          </label>
        ) : null}

        <select
          ref={ref}
          id={selectId}
          aria-invalid={Boolean(error)}
          className={clsx(
            'w-full rounded-xl border border-agentic-gray-300 bg-white px-4 py-3 text-agentic-text-primary transition',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kv-agentic-blue focus-visible:ring-offset-2',
            error && 'border-red-300 focus-visible:ring-red-500',
            'disabled:cursor-not-allowed disabled:bg-agentic-gray-100 disabled:text-agentic-text-muted',
            className
          )}
          {...props}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        {error ? <p className="mt-1 text-sm text-red-600">{error}</p> : null}
        {helperText && !error ? <p className="mt-1 text-sm text-agentic-text-secondary">{helperText}</p> : null}
      </div>
    );
  }
);

Select.displayName = 'Select';
